import { useState, useEffect, useContext } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { FiFilter, FiInfo, FiDownload } from "react-icons/fi";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Loader,
  CommonTable,
  DownloadExcelBill,
} from "../../Components/Common";
import SearchableDropdown from "../../Components/Common/SearchableDropdown";
import {
  getAttendanceList,
  reviewAttendance,
} from "../../Services/attendanceService";
import {
  ADMIN_ATTENDANCE_COLUMN_LABELS,
  ADMIN_ATTENDANCE_TABLE_COLUMNS,
  ACTION_APPROVED,
  ACTION_REJECTED,
  APPROVED,
  ATTENDANCE_ERROR,
  ATTENDANCE_STATUS_ERROR,
  REJECTED,
  USERDATA_ERROR,
  MESSAGES,
  PENDING_L1_APPROVAL,
  PENDING_L2_APPROVAL,
  EXCEL_FILE_COLUMNS,
  AUTO_APPROVED,
  CORRECTED,
} from "./constants";
import RejectPopup from "./Component/RejectPopup";
import { GET_ALL_PAGINATION, RESET_PAGINATION } from "../../Constants/Strings";
import InfiniteScroll from "react-infinite-scroll-component";
import { getUniqueDataById, formatDateDDMMYYYY } from "../../Utils";
import { getAllUsers } from "../../Services/UserService";
import MobileAttendanceCard from "./Component/MobileAttendanceCard";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { getSites } from "../../Services/siteService";
import { UserContext } from "../../Context";
import { USER_ROLES } from "../../Constants/Strings";


// TODO: clear filter should be disable if not filters are applied with different colot.
// TODO: reset filter after clear. Filter should have option for all user as default
// TODO: details page for the view attendane
// TODO: sticky header in the common table

const AdminAttendanceList = () => {
  const { user } = useContext(UserContext);
  const canViewOtherUsersAttendance = [
    USER_ROLES.ADMIN,
    USER_ROLES.HR,
    USER_ROLES.DIRECTOR_MANAGEMENT,
    USER_ROLES.INCHARGE,
    USER_ROLES.PROJECT_INCHARGE,
  ].includes(user?.role);
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeRejectRow, setActiveRejectRow] = useState(null); // will hold the row object or at least the ID
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedUser, setSelectedUsers] = useState(null);
  const [selectedSiteObj, setSelectedSiteObj] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(() => {
    return location.state?.filters || { status: "", date: null, user: "", site: "",is_backdated: "" };
  });

  const getPaginationData = () => {
    if (pagination.offset > pagination.count) return;
    const finalParams = { ...pagination, ...filters };
    fetchAttendanceData(finalParams);
  };

  // Function to fetch attendance data
  const fetchAttendanceData = async (finalParams, isInitial = false) => {
    setLoading(true);
    try {
      const response = await getAttendanceList({
        ...finalParams,
      });
      if (response.status === 200) {
        if (isInitial) {
          setAttendanceData(response.data.results || []);
        } else {
          setAttendanceData((prevData) =>
            getUniqueDataById(prevData, response.data.results),
          );
        }
        setPagination((prevData) => ({
          ...prevData,
          count: response.data.count,
          offset: prevData.offset + prevData.limit,
        }));
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || ATTENDANCE_ERROR;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersForDropdown = async ({ search }) => {
    try {
      const response = await getAllUsers({
        ...GET_ALL_PAGINATION,
        search,
      });

      if (response.status === 200) {
        return response.data?.results || [];
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const fetchSites = async (searchText) => {
      const search =
        typeof searchText === "string" ? searchText : searchText?.search || "";
  
      const res = await getSites({ search });
      return res?.data?.results || [];
    };

  useEffect(() => {
    fetchAttendanceData({ ...RESET_PAGINATION, ...filters }, true);
    fetchUsersForDropdown();
  }, []);

  const handleApproval = async (id, action, rejection_reason) => {
    setLoading(true);
    try {
      let review_data = {
        action,
        rejection_reason,
      };
      const response = await reviewAttendance(id, review_data);
      setAttendanceData([]);
      if (response.status === 200) {
        toast.success(`Attendance submitted successfully`);
      } else {
        const msg = extractErrorMessage(response) || ATTENDANCE_STATUS_ERROR;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || ATTENDANCE_STATUS_ERROR;
      toast.error(msg);
    } finally {
      setLoading(false);
      await fetchAttendanceData(RESET_PAGINATION, true);
    }
  };

  const downloadExcelData = () => {
    DownloadExcelBill({
      fetchFn: getAttendanceList,
      fieldMappings: EXCEL_FILE_COLUMNS,
      fileName: "Attendance_Data.xlsx",
    });
  };

  const statusFunction = (row) => {
    return (
      <div className="flex items-center">
        <div>
          <div>
            <Link
              to={`/attendance/view_detailed/${row.id}`}
              title="View Detailed"
              className="text-primary mb-1 hover:text-secondary"
              state={{ filters, from: location.pathname }}
            >
              <FiInfo className="text-lg" />
            </Link>
          </div>
        </div>

        {/* Approve Button */}
        {canViewOtherUsersAttendance && (
      <>
        <div className="relative group">
          <button
            className={`${
              row.status === APPROVED ||
              row.status === REJECTED ||
              row.status === AUTO_APPROVED ||
              row.status === CORRECTED
                ? "text-gray-400"
                : "text-primary"
            }  p-2 rounded-full`}
            disabled={
              row.status === APPROVED ||
              row.status === REJECTED ||
              row.status === AUTO_APPROVED ||
              row.status === CORRECTED
            }
            onClick={() => handleApproval(row.id, ACTION_APPROVED)}
          >
            <FaCheck className="text-xl" />
          </button>
          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 text-xs bg-secondary text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            Approve
          </span>
        </div>

        {/* Reject Button */}
        <div className="relative group">
          <button
            className={`${
              row.status === APPROVED ||
              row.status === REJECTED ||
              row.status === AUTO_APPROVED ||
              row.status === CORRECTED
                ? "text-gray-400"
                : "text-red-500"
            }  p-2 rounded-full`}
            disabled={
              row.status === APPROVED ||
              row.status === REJECTED ||
              row.status === AUTO_APPROVED ||
              row.status === CORRECTED
            }
            onClick={() => setActiveRejectRow(row)}
          >
            <FaTimes className="text-xl" />
          </button>
          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 text-xs bg-secondary text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            Reject
          </span>
        </div>
      </>
      )}

        {/* Popup only for selected row */}
        {activeRejectRow?.id === row.id && (
          <div className="inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center absolute">
            <RejectPopup
              id={row.id}
              action={ACTION_REJECTED}
              handleApproval={handleApproval}
              setActiveRejectRow={() => setActiveRejectRow(null)}
            />
          </div>
        )}
      </div>
    );
  };

  const renderField = (key, row) => {
    if(key === "is_backdated"){
      return row.is_backdated === "Yes" ? (
          <span className="text-primary font-semibold">Yes</span>
        ) : <span className="text-grey-600 font-base">No</span>;
    }
    if (key === "status") {
      switch (row.status) {
        case APPROVED:
          return <span className="text-green-600 font-medium">Approved</span>;
        case AUTO_APPROVED:
          return (
            <span className="text-green-600 font-medium">Auto Approved</span>
          );
        case REJECTED:
          return <span className="text-red-600 font-medium">Rejected</span>;
        case PENDING_L1_APPROVAL:
          return (
            <span className="text-grey-600 font-medium">
              Pending Approval
            </span>
          );
        case PENDING_L2_APPROVAL:
          return (
            <span className="text-grey-600 font-medium">
              Pending L2 Approval
            </span>
          );
        case CORRECTED:
          return <span className="text-blue-600 font-medium">Corrected</span>;
        default:
          return <span className="text-gray-500">Pending Approval</span>;
      }
    }

    if (key === "actions") {
      return statusFunction(row);
    }

    if (key === "site_name") {
      return row.site_name || "Non-Site";
    }

    if (key === "date") {
      return formatDateDDMMYYYY(row.date) || row.date;
    }

    if (
      ["checkin_time", "checkout_time"].includes(key) &&
      typeof row[key] === "string"
    ) {
      return row[key].split(".")[0];
    }

    return row[key];
  };

  return (
    <div className="px-4 py-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold mb-4 text-primary">
          Attendance Actions
        </h2>
        <div>
          {showFilter && (
            <div className="inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center absolute">
              <div className="bg-white w-full max-w-md mx-4 rounded-2xl shadow-xl p-6 relative">
                {/* Title */}
                <h3 className="text-lg font-semibold text-primary mb-4">
                  Filter Attendance
                </h3>

                {/* Filter Form */}
                <div className="flex flex-col gap-4">

                  {/* Users dropdown*/}
                  {canViewOtherUsersAttendance && (
                    <SearchableDropdown
                      label="Users"
                      fetchFunction={fetchUsersForDropdown}
                      valueKey="id"
                      labelKey="full_name"
                      placeholder="All Users"
                      search_box_placeholder="Search User"
                      includeAllOption={true}
                      allOptionLabel="All Users"
                      value={selectedUser}
                      onSelect={(selected) => {
                        setSelectedUsers(selected);
                        setFilters((prev) => ({
                          ...prev,
                          user: selected?.id || "",
                        }));
                      }}
                    />
                  )}

                  {/* Backdated  */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Backdated
                    </label>

                    <select
                      value={filters.is_backdated}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          is_backdated: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-1 focus:ring-primary focus:outline-none"
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  {/* Status Dropdown */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>

                    <select
                      value={filters.status}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-1 focus:ring-primary focus:outline-none"
                    >
                      <option value="">All</option>
                      <option value={APPROVED}>Approved</option>
                      <option value={AUTO_APPROVED}>Auto Approved</option>
                      <option value={REJECTED}>Rejected</option>
                      <option value={PENDING_L1_APPROVAL}>
                        Pending Approval
                      </option>
                      <option value={PENDING_L2_APPROVAL}>
                        Pending L2 Approval
                      </option>
                    </select>
                  </div>

                  {/* SITE FILTER */}
                  <SearchableDropdown
                    label="Site"
                    placeholder="Search site"
                    fetchFunction={fetchSites}
                    labelKey="name"
                    valueKey="id"
                    includeAllOption
                    value={selectedSiteObj}
                    onSelect={(site) => {
                      setSelectedSiteObj(site);
                      setFilters((prev) => ({
                        ...prev,
                        site: site?.id || "",
                      }));
                    }}
                  />
                  
                  {/*Select Date*/}
                  <div className="flex flex-col gap-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1 pr-2">
                      Date
                    </label>
                    <input
                      name={filters.date}
                      type="date"
                      value={filters.date || ""}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          date: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                    />
                  </div>

                  {/* Apply / Clear Buttons */}
                  <div className="flex justify-between items-center mt-4">
                    <button
                      className=" px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm"
                      onClick={() => {
                        const cleared = { status: "", date: null, user: "", site: "" };
                        setSelectedUsers(null);
                        setFilters(cleared);
                        setSelectedSiteObj(null);
                        setAttendanceData([]);
                        fetchAttendanceData(
                          { ...cleared, ...RESET_PAGINATION },
                          true,
                        );
                        // Update state so coming back doesn't restore old filters
                        navigate(".", {
                          state: { filters: cleared },
                          replace: true,
                        });
                      }}
                    >
                      Clear
                    </button>
                    <div className="flex gap-2">
                      <button
                        className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm text-gray-800"
                        onClick={() => {
                          setShowFilter(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-4 py-2 rounded-lg bg-primary hover:bg-green-600 text-white text-sm"
                        onClick={() => {
                          setAttendanceData([]);

                          fetchAttendanceData(
                            { ...filters, ...RESET_PAGINATION },
                            true,
                          );
                          // Update navigation state with latest filters
                          navigate(".", { state: { filters }, replace: true });
                          setShowFilter(false);
                        }}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/*filter icons */}
        <div className="flex gap-2 justify-between items-center mb-4">
          <button
            className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
            onClick={() => downloadExcelData()}
          >
            <FiDownload className="text-xl" />
          </button>

          <button
            className=" px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold  shadow-lg flex itmes-center justify-between gap-1"
            onClick={() => setShowFilter(!showFilter)}
          >
            <FiFilter className="text-2xl" />
          </button>
        </div>
      </div>
      {loading && attendanceData.length === 0 ? (
        <div className="flex justify-center pt-10">
          <Loader size={4} />
        </div>
      ) : attendanceData.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          {MESSAGES.NO_REQUESTS_AVAILABLE}
        </div>
      ) : (
        <>
          {/* For Mobile View */}
          <div className="block md:hidden">
            <InfiniteScroll
              dataLength={attendanceData.length}
              next={getPaginationData}
              hasMore={pagination.offset <= pagination.count}
              scrollableTarget="mobileScrollableDiv"
            >
              <div
                className="h-[70vh] overflow-y-auto"
                id="mobileScrollableDiv"
              >
                {attendanceData.map((item) => (
                  <MobileAttendanceCard
                    key={item.id}
                    data={item}
                    displayActionButtons={true}
                    handleApproval={handleApproval}
                    setActiveRejectRow={setActiveRejectRow}
                    activeRejectRow={activeRejectRow}
                  />
                ))}
              </div>
            </InfiniteScroll>
          </div>

          {/* Desktop View */}
          <div className="hidden md:block">
            <InfiniteScroll
              dataLength={attendanceData.length}
              next={getPaginationData}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="scrollDiv"
            >
              <div
                className="h-[70vh] overflow-x-auto overflow-y-auto border rounded-md"
                id="scrollDiv"
              >
                <CommonTable
                  tableColumns={ADMIN_ATTENDANCE_TABLE_COLUMNS}
                  columnLabels={ADMIN_ATTENDANCE_COLUMN_LABELS}
                  tableRows={attendanceData}
                  renderField={renderField}
                  headerClassName="bg-gray-100"
                  rowClick={null}
                  sortingColumn="checkin_time"
                />
              </div>
            </InfiniteScroll>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAttendanceList;
